import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as adminAPI from '../../api/api_admin';

// ===========================================
// ASYNC THUNKS
// ===========================================

// User Management
export const fetchAllUsers = createAsyncThunk(
    'admin/fetchAllUsers',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getAllUsers(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch users');
        }
    }
);

export const fetchUserById = createAsyncThunk(
    'admin/fetchUserById',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getUserById(userId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch user');
        }
    }
);

export const updateUser = createAsyncThunk(
    'admin/updateUser',
    async ({ userId, userData }, { rejectWithValue }) => {
        try {
            const response = await adminAPI.updateUser(userId, userData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to update user');
        }
    }
);

export const deactivateUser = createAsyncThunk(
    'admin/deactivateUser',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await adminAPI.deactivateUser(userId);
            return { userId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to deactivate user');
        }
    }
);

export const resetUserCode = createAsyncThunk(
    'admin/resetUserCode',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await adminAPI.resetUserCode(userId);
            return { userId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to reset user code');
        }
    }
);

// System Administration
export const fetchSystemStats = createAsyncThunk(
    'admin/fetchSystemStats',
    async (_, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getSystemStats();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch system stats');
        }
    }
);

export const fetchSystemHealth = createAsyncThunk(
    'admin/fetchSystemHealth',
    async (_, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getSystemHealth();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch system health');
        }
    }
);

export const clearSystemCache = createAsyncThunk(
    'admin/clearSystemCache',
    async (_, { rejectWithValue }) => {
        try {
            const response = await adminAPI.clearSystemCache();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to clear cache');
        }
    }
);

// Analytics
export const fetchUserAnalytics = createAsyncThunk(
    'admin/fetchUserAnalytics',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getUserAnalytics(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch user analytics');
        }
    }
);

export const fetchRecipeAnalytics = createAsyncThunk(
    'admin/fetchRecipeAnalytics',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getRecipeAnalytics(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch recipe analytics');
        }
    }
);

export const fetchSustainabilityAnalytics = createAsyncThunk(
    'admin/fetchSustainabilityAnalytics',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getSustainabilityAnalytics(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch sustainability analytics');
        }
    }
);

export const fetchPerformanceAnalytics = createAsyncThunk(
    'admin/fetchPerformanceAnalytics',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getPerformanceAnalytics(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch performance analytics');
        }
    }
);

// Security
export const fetchSecurityDashboard = createAsyncThunk(
    'admin/fetchSecurityDashboard',
    async (_, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getSecurityDashboard();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch security dashboard');
        }
    }
);

export const fetchAuditLogs = createAsyncThunk(
    'admin/fetchAuditLogs',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getAuditLogs(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch audit logs');
        }
    }
);

// Settings
export const fetchSystemSettings = createAsyncThunk(
    'admin/fetchSystemSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await adminAPI.getSystemSettings();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch system settings');
        }
    }
);

export const updateSystemSettings = createAsyncThunk(
    'admin/updateSystemSettings',
    async (settingsData, { rejectWithValue }) => {
        try {
            const response = await adminAPI.updateSystemSettings(settingsData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to update system settings');
        }
    }
);

// ===========================================
// SLICE
// ===========================================

const initialState = {
    // User Management
    users: {
        list: [],
        pagination: {},
        loading: false,
        error: null,
        selectedUser: null,
        userActivity: null,
    },
    
    // System Administration
    system: {
        stats: null,
        health: null,
        logs: [],
        backups: [],
        loading: false,
        error: null,
    },
    
    // Analytics
    analytics: {
        users: null,
        recipes: null,
        sustainability: null,
        performance: null,
        loading: false,
        error: null,
    },
    
    // Security
    security: {
        dashboard: null,
        auditLogs: [],
        failedLogins: [],
        suspiciousActivity: [],
        loading: false,
        error: null,
    },
    
    // Settings
    settings: {
        system: null,
        features: null,
        integrations: null,
        email: null,
        notifications: null,
        loading: false,
        error: null,
    },
    
    // UI State
    ui: {
        activeTab: 'dashboard',
        sidebarCollapsed: false,
        theme: 'light',
        notifications: [],
    }
};

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        // UI Actions
        setActiveTab: (state, action) => {
            state.ui.activeTab = action.payload;
        },
        toggleSidebar: (state) => {
            state.ui.sidebarCollapsed = !state.ui.sidebarCollapsed;
        },
        setTheme: (state, action) => {
            state.ui.theme = action.payload;
        },
        addNotification: (state, action) => {
            state.ui.notifications.push({
                id: Date.now(),
                type: action.payload.type || 'info',
                message: action.payload.message,
                timestamp: new Date().toISOString(),
            });
        },
        removeNotification: (state, action) => {
            state.ui.notifications = state.ui.notifications.filter(
                notification => notification.id !== action.payload
            );
        },
        clearNotifications: (state) => {
            state.ui.notifications = [];
        },
        
        // User Management Actions
        setSelectedUser: (state, action) => {
            state.users.selectedUser = action.payload;
        },
        clearUserError: (state) => {
            state.users.error = null;
        },
        
        // System Actions
        clearSystemError: (state) => {
            state.system.error = null;
        },
        
        // Analytics Actions
        clearAnalyticsError: (state) => {
            state.analytics.error = null;
        },
        
        // Security Actions
        clearSecurityError: (state) => {
            state.security.error = null;
        },
        
        // Settings Actions
        clearSettingsError: (state) => {
            state.settings.error = null;
        },
    },
    extraReducers: (builder) => {
        // User Management
        builder
            .addCase(fetchAllUsers.pending, (state) => {
                state.users.loading = true;
                state.users.error = null;
            })
            .addCase(fetchAllUsers.fulfilled, (state, action) => {
                state.users.loading = false;
                state.users.list = action.payload.data || [];
                state.users.pagination = action.payload.pagination || {};
            })
            .addCase(fetchAllUsers.rejected, (state, action) => {
                state.users.loading = false;
                state.users.error = action.payload;
            })
            .addCase(fetchUserById.fulfilled, (state, action) => {
                state.users.selectedUser = action.payload.data;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                const updatedUser = action.payload.data;
                const index = state.users.list.findIndex(user => user.id === updatedUser.id);
                if (index !== -1) {
                    state.users.list[index] = updatedUser;
                }
            })
            .addCase(deactivateUser.fulfilled, (state, action) => {
                const userId = action.payload.userId;
                state.users.list = state.users.list.filter(user => user.id !== userId);
            });
            
        // System Administration
        builder
            .addCase(fetchSystemStats.pending, (state) => {
                state.system.loading = true;
                state.system.error = null;
            })
            .addCase(fetchSystemStats.fulfilled, (state, action) => {
                state.system.loading = false;
                state.system.stats = action.payload;
            })
            .addCase(fetchSystemStats.rejected, (state, action) => {
                state.system.loading = false;
                state.system.error = action.payload;
            })
            .addCase(fetchSystemHealth.fulfilled, (state, action) => {
                state.system.health = action.payload;
            })
            .addCase(clearSystemCache.fulfilled, (state, action) => {
                // Add success notification
                state.ui.notifications.push({
                    id: Date.now(),
                    type: 'success',
                    message: 'System cache cleared successfully',
                    timestamp: new Date().toISOString(),
                });
            });
            
        // Analytics
        builder
            .addCase(fetchUserAnalytics.pending, (state) => {
                state.analytics.loading = true;
                state.analytics.error = null;
            })
            .addCase(fetchUserAnalytics.fulfilled, (state, action) => {
                state.analytics.loading = false;
                state.analytics.users = action.payload;
            })
            .addCase(fetchUserAnalytics.rejected, (state, action) => {
                state.analytics.loading = false;
                state.analytics.error = action.payload;
            })
            .addCase(fetchRecipeAnalytics.fulfilled, (state, action) => {
                state.analytics.recipes = action.payload;
            })
            .addCase(fetchSustainabilityAnalytics.fulfilled, (state, action) => {
                state.analytics.sustainability = action.payload;
            })
            .addCase(fetchPerformanceAnalytics.fulfilled, (state, action) => {
                state.analytics.performance = action.payload;
            });
            
        // Security
        builder
            .addCase(fetchSecurityDashboard.pending, (state) => {
                state.security.loading = true;
                state.security.error = null;
            })
            .addCase(fetchSecurityDashboard.fulfilled, (state, action) => {
                state.security.loading = false;
                state.security.dashboard = action.payload;
            })
            .addCase(fetchSecurityDashboard.rejected, (state, action) => {
                state.security.loading = false;
                state.security.error = action.payload;
            })
            .addCase(fetchAuditLogs.fulfilled, (state, action) => {
                state.security.auditLogs = action.payload.data || [];
            });
            
        // Settings
        builder
            .addCase(fetchSystemSettings.pending, (state) => {
                state.settings.loading = true;
                state.settings.error = null;
            })
            .addCase(fetchSystemSettings.fulfilled, (state, action) => {
                state.settings.loading = false;
                state.settings.system = action.payload;
            })
            .addCase(fetchSystemSettings.rejected, (state, action) => {
                state.settings.loading = false;
                state.settings.error = action.payload;
            })
            .addCase(updateSystemSettings.fulfilled, (state, action) => {
                state.settings.system = action.payload;
                // Add success notification
                state.ui.notifications.push({
                    id: Date.now(),
                    type: 'success',
                    message: 'System settings updated successfully',
                    timestamp: new Date().toISOString(),
                });
            });
    },
});

export const {
    setActiveTab,
    toggleSidebar,
    setTheme,
    addNotification,
    removeNotification,
    clearNotifications,
    setSelectedUser,
    clearUserError,
    clearSystemError,
    clearAnalyticsError,
    clearSecurityError,
    clearSettingsError,
} = adminSlice.actions;

export default adminSlice.reducer;
